import { useSFConnectionState } from '@contexts/sf-connection-context.js';
import {
  CustomObjectDownloadStatus,
  MetadataSyncResult,
  SalesforceMetadataSyncResult,
} from '@modules/salesforce/models';
import { useCallback, useEffect, useRef } from 'react';
import { SalesforceMetadataSyncResultsService } from '@modules/salesforce/services';
import { usePolling } from './use-polling';

// Global state to keep polling active even when components unmount
const globalPollingState = {
  isPolling: false,
  connectionId: null,
  intervalId: null,
  callbacks: {
    onSuccess: null,
    onFailure: null,
    onPartiallySuccess: null,
  },
};

interface UseCheckMetadataInitialSyncStatusProps {
  /** Callback triggered upon initial sync success. */
  onSuccess: () => void;
  /** Callback triggered upon initial sync failure. */
  onFailure: () => void;
  /** Callback triggered upon customObjectDownloadStatus success. */
  onPartiallySuccess: () => void;
}

const useCheckMetadataInitialSyncStatus = (
  props: UseCheckMetadataInitialSyncStatusProps,
) => {
  const { onSuccess, onFailure, onPartiallySuccess } = props;

  // Store callbacks in global state
  globalPollingState.callbacks.onSuccess = onSuccess;
  globalPollingState.callbacks.onFailure = onFailure;
  globalPollingState.callbacks.onPartiallySuccess = onPartiallySuccess;

  const { selectedConnection, initialSyncResult, handleMetadataSyncResult } =
    useSFConnectionState();

  const onSyncResultChange = useCallback(
    (result: SalesforceMetadataSyncResult) => {
      if (result?.metadataSyncResult == MetadataSyncResult.SUCCESS) {
        globalPollingState.callbacks.onSuccess?.();
      } else if (result?.metadataSyncResult == MetadataSyncResult.FAILED) {
        globalPollingState.callbacks.onFailure?.();
      } else if (
        result?.customObjectDownloadStatus == CustomObjectDownloadStatus.SUCCESS
      ) {
        globalPollingState.callbacks.onPartiallySuccess?.();
      }
      handleMetadataSyncResult(result?.metadataSyncResult);
    },
    [handleMetadataSyncResult],
  );

  const fetchSyncStatus = useCallback(() => {
    const connectionId =
      globalPollingState.connectionId || selectedConnection?.id;
    if (!connectionId) {
      return;
    }

    SalesforceMetadataSyncResultsService.findAll(
      `,salesforceConnectionId:${connectionId},metadataSyncType:INITIAL_SYNC`,
      undefined,
      undefined,
      'id,desc',
    )
      .then((res) => {
        if (res?.content?.length) {
          const result = res?.content[0];
          onSyncResultChange(result);

          // Stop polling if we get a final result
          if (
            result?.metadataSyncResult === MetadataSyncResult.SUCCESS ||
            result?.metadataSyncResult === MetadataSyncResult.FAILED
          ) {
            if (globalPollingState.intervalId) {
              clearInterval(globalPollingState.intervalId);
              globalPollingState.intervalId = null;
              globalPollingState.isPolling = false;
            }
          }
        }
      })
      .catch(() => {});
  }, [onSyncResultChange, selectedConnection?.id]);

  const { startPolling, stopPolling } = usePolling(fetchSyncStatus, 5000);

  // Only stop polling on success or failure, not on component unmount
  useEffect(() => {
    if (
      initialSyncResult === MetadataSyncResult.FAILED ||
      initialSyncResult === MetadataSyncResult.SUCCESS
    ) {
      stopPolling();
      globalPollingState.isPolling = false;
      globalPollingState.intervalId = null;
    }
  }, [stopPolling, initialSyncResult]);

  const handleStartInitialSyncPolling = useCallback(() => {
    // Only start polling if it's not already running
    if (!globalPollingState.isPolling) {
      console.log('Starting initial sync polling for', selectedConnection?.id);
      globalPollingState.connectionId = selectedConnection?.id;
      globalPollingState.isPolling = true;

      // Set the polling state in the global object
      const id = setInterval(fetchSyncStatus, 5000);
      globalPollingState.intervalId = id;

      // Run immediately
      fetchSyncStatus();
    } else {
      console.log('Polling already in progress, not starting a new poll');
    }
  }, [fetchSyncStatus, selectedConnection?.id]);

  // Don't stop polling on component unmount
  useEffect(() => {
    return () => {
      console.log('Component unmounted, but polling will continue');
      // We don't clean up the interval here, allowing polling to continue
    };
  }, []);

  return {
    handleStartInitialSyncPolling,
  };
};

export { useCheckMetadataInitialSyncStatus };
